import { useSyncExternalStore } from 'react';

/**
 * One shared clock per interval for every live-updating label: a single timer,
 * however many components subscribe, stopped when the last one unmounts.
 */
interface Clock {
  now: number;
  listeners: Set<() => void>;
  timer: ReturnType<typeof setInterval> | null;
  subscribe: (onChange: () => void) => () => void;
  get: () => number;
}

const clocks = new Map<number, Clock>();

function clockFor(intervalMs: number): Clock {
  const existing = clocks.get(intervalMs);
  if (existing) return existing;
  const c: Clock = {
    now: Date.now(),
    listeners: new Set(),
    timer: null,
    // Stable identities: useSyncExternalStore resubscribes whenever `subscribe` changes.
    subscribe: (onChange) => {
      c.listeners.add(onChange);
      if (!c.timer) {
        // An idle clock catches up when its first subscriber arrives (React re-reads after subscribing).
        c.now = Date.now();
        c.timer = setInterval(() => {
          c.now = Date.now();
          for (const l of c.listeners) l();
        }, intervalMs);
      }
      return () => {
        c.listeners.delete(onChange);
        if (c.listeners.size === 0 && c.timer) {
          clearInterval(c.timer);
          c.timer = null;
        }
      };
    },
    get: () => c.now,
  };
  clocks.set(intervalMs, c);
  return c;
}

/** The current time, re-rendering every `intervalMs` (default 1 s). */
export function useNow(intervalMs = 1000): number {
  const c = clockFor(intervalMs);
  return useSyncExternalStore(c.subscribe, c.get, c.get);
}
